import React, { useCallback, useMemo, useState } from 'react';
import {
  ColumnPicker, ConfirmDialog, DataTable, DensityToggle, EmptyState, FilterBar, Pagination,
  SearchInput, SideDrawer, Toolbar, exportFilename, exportRows, useTableDensity, useToast,
  type ActiveFilter, type Column, type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  CATEGORY_LABEL, MEMBER_TYPE_LABEL, QUOTA_RULES, QUOTA_USAGE,
  type MemberType, type ProductCategory, type QuotaRule, type QuotaUsage,
} from './memberTypeData';

/**
 * QuotaScreen — ตั้งค่าและติดตามโควตาการซื้อรายปีของกลุ่ม ปตท. (FR-ADM-17, BR-09)
 *
 * BR-09 จำกัดปริมาณการซื้อในราคาพนักงานต่อคนต่อปี เพื่อกันการซื้อไปขายต่อ
 * หน้านี้ทำสองอย่างที่แยกกันชัดเจน:
 *
 *   แท็บ "กติกาโควตา"  — ตั้งเพดานแยกตามหมวดสินค้า × ประเภทสมาชิก
 *                        การแก้เพดานกระทบสมาชิกทุกคนในกลุ่มนั้นทันที จึงต้องยืนยันพร้อมเหตุผล
 *                        และหน้าจอบอกจำนวนคนที่ "จะเกินเพดานใหม่ทันที" ก่อนกดบันทึก
 *
 *   แท็บ "การใช้โควตา" — ดูว่าใครใช้ไปเท่าไร เรียงจากใกล้เต็มที่สุด
 *                        คนที่เต็มแล้วคือคนที่ระบบบล็อกการสั่งซื้อ ซึ่งจะโทรมาถามที่ call center
 *
 * ⛔ ตัวเลขเพดานทุกค่ายังเป็นค่าสมมติ (PROGRESS-REPORT 8.4) — กำกับไว้ทุกแถวด้วยป้าย "ค่าสมมติ"
 *    ไม่ใช่แค่ในเอกสาร เพราะคนที่เปิดหน้านี้ต้องรู้ว่ายังห้ามใช้ตัดสินใจ
 */

type ScreenState = 'ready' | 'loading' | 'error';
type Tab = 'rules' | 'usage';

const PTT_TYPES: MemberType[] = ['ptt-employee', 'ptt-group', 'ptt-retired'];

const pctOf = (u: QuotaUsage) => Math.round((u.used / u.limit) * 100);

/** แถบแสดงการใช้โควตา — มีตัวเลขและคำกำกับ ไม่สื่อด้วยสีเดียว */
const UsageBar: React.FC<{ used: number; limit: number; warnAt?: number }> = ({ used, limit, warnAt = 80 }) => {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const full = used >= limit;
  const near = !full && pct >= warnAt;
  const barColor = full ? 'var(--error-solid)' : near ? 'var(--warning-solid)' : 'var(--blue-700)';
  return (
    <span className="flex items-center gap-[var(--space-2)]">
      <span className="bo-num w-[92px] shrink-0" style={{ textAlign: 'right' }}>
        {used.toLocaleString('th-TH')} / {limit.toLocaleString('th-TH')}
      </span>
      <span
        role="img"
        aria-label={`ใช้ไป ${pct}% ของโควตา`}
        className="h-[8px] flex-1 overflow-hidden rounded-[var(--radius-pill)] bg-[var(--surface-100)]"
      >
        <span className="block h-full rounded-[var(--radius-pill)]" style={{ width: `${pct}%`, background: barColor }} />
      </span>
      <span className="bo-num w-[44px] shrink-0 text-[length:var(--text-table-sm)]" style={{ textAlign: 'right' }}>{pct}%</span>
      {full ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
          <Icon name="block" size={14} filled />เต็ม
        </span>
      ) : near ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
          <Icon name="warning" size={14} filled />ใกล้เต็ม
        </span>
      ) : null}
    </span>
  );
};

const ProvisionalTag: React.FC = () => (
  <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--warning-bg)] px-[var(--space-15)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--warning-text)]">
    <Icon name="pending_actions" size={13} />
    ค่าสมมติ
  </span>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{children}</span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

const inputCls =
  'min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

interface EditRule {
  rule: QuotaRule;
  limitPerYear: number | null;
  warnAtPercent: number;
  enabled: boolean;
}

export interface QuotaScreenProps {
  initialState?: ScreenState;
}

export const QuotaScreen: React.FC<QuotaScreenProps> = ({ initialState = 'ready' }) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [rules, setRules] = useState<QuotaRule[]>(QUOTA_RULES);
  const [tab, setTab] = useState<Tab>('rules');
  const [density, setDensity] = useTableDensity();

  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<'all' | ProductCategory>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | MemberType>('all');
  const [usageFilter, setUsageFilter] = useState<'all' | 'full' | 'near'>('all');
  const [ruleSort, setRuleSort] = useState<SortState | null>({ columnId: 'category', direction: 'asc' });
  const [usageSort, setUsageSort] = useState<SortState | null>({ columnId: 'pct', direction: 'desc' });
  const [visibleRuleCols, setVisibleRuleCols] = useState<string[]>([]);
  const [visibleUsageCols, setVisibleUsageCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [edit, setEdit] = useState<EditRule | null>(null);
  const [confirmSave, setConfirmSave] = useState<EditRule | null>(null);
  const [usageDetail, setUsageDetail] = useState<QuotaUsage | null>(null);

  const limitOf = useCallback(
    (cat: ProductCategory, type: MemberType) =>
      rules.find((r) => r.category === cat && r.memberType === type),
    [rules],
  );

  /* ----------------------------------------------------- กติกาโควตา */

  const filteredRules = useMemo(() => rules.filter((r) => {
    if (catFilter !== 'all' && r.category !== catFilter) return false;
    if (typeFilter !== 'all' && r.memberType !== typeFilter) return false;
    if (query) {
      const q = query.trim();
      if (!CATEGORY_LABEL[r.category].includes(q) && !MEMBER_TYPE_LABEL[r.memberType].includes(q)) return false;
    }
    return true;
  }), [rules, catFilter, typeFilter, query]);

  const sortedRules = useMemo(() => {
    if (!ruleSort) return filteredRules;
    const dir = ruleSort.direction === 'asc' ? 1 : -1;
    const key = (r: QuotaRule): string | number =>
      ruleSort.columnId === 'limitPerYear' ? (r.limitPerYear ?? Number.MAX_SAFE_INTEGER)
        : ruleSort.columnId === 'memberType' ? MEMBER_TYPE_LABEL[r.memberType]
          : CATEGORY_LABEL[r.category];
    return [...filteredRules].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filteredRules, ruleSort]);

  /** จำนวนสมาชิกที่จะเกินเพดานใหม่ทันทีถ้าบันทึกค่านี้ */
  const overflowCount = (e: EditRule) => {
    if (e.limitPerYear === null || !e.enabled) return 0;
    return QUOTA_USAGE.filter((u) => u.category === e.rule.category && u.memberType === e.rule.memberType && u.used > e.limitPerYear!).length;
  };

  const ruleColumns: Array<Column<QuotaRule>> = useMemo(() => [
    { id: 'category', header: 'หมวดสินค้า', width: 168, pinned: true, required: true, sortable: true, value: (r) => CATEGORY_LABEL[r.category] },
    {
      id: 'memberType', header: 'ประเภทสมาชิก', width: 186, sortable: true, value: (r) => MEMBER_TYPE_LABEL[r.memberType],
      cell: (r) => (
        <span className="inline-flex items-center gap-1">
          {MEMBER_TYPE_LABEL[r.memberType]}
          {r.memberType === 'ptt-retired' && (
            <span className="text-[length:var(--text-table-sm)] text-[var(--warning-text)]">ยังไม่มีนโยบาย</span>
          )}
        </span>
      ),
    },
    {
      id: 'limitPerYear', header: 'เพดานต่อคนต่อปี (ชิ้น)', width: 176, numeric: true, sortable: true,
      value: (r) => r.limitPerYear ?? '',
      cell: (r) => (r.limitPerYear === null
        ? <span className="text-[var(--ink-600)]">ไม่จำกัด</span>
        : <span className="bo-num font-[var(--weight-semibold)]">{r.limitPerYear.toLocaleString('th-TH')}</span>),
    },
    {
      id: 'warnAtPercent', header: 'เตือนผู้ใช้เมื่อถึง', width: 140, numeric: true,
      value: (r) => r.warnAtPercent,
      cell: (r) => (r.limitPerYear === null ? <span className="text-[var(--ink-400)]">—</span> : <span className="bo-num">{r.warnAtPercent}%</span>),
    },
    {
      id: 'enabled', header: 'สถานะ', width: 128,
      value: (r) => (r.enabled ? 'บังคับใช้' : 'ไม่บังคับใช้'),
      cell: (r) => (r.enabled
        ? <span className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] border border-[var(--success-border)] bg-[var(--success-bg)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] text-[var(--success-text)]"><Icon name="check_circle" size={14} filled />บังคับใช้</span>
        : <span className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] border border-[var(--line-300)] bg-[var(--surface-100)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]"><Icon name="block" size={14} filled />ไม่บังคับใช้</span>),
    },
    {
      id: 'affected', header: 'สมาชิกที่ใกล้เต็มหรือเต็มแล้ว', width: 216,
      value: (r) => {
        const rows = QUOTA_USAGE.filter((u) => u.category === r.category && u.memberType === r.memberType);
        return rows.filter((u) => pctOf(u) >= r.warnAtPercent).length;
      },
      cell: (r) => {
        const rows = QUOTA_USAGE.filter((u) => u.category === r.category && u.memberType === r.memberType);
        const full = rows.filter((u) => u.used >= u.limit).length;
        const near = rows.filter((u) => u.used < u.limit && pctOf(u) >= r.warnAtPercent).length;
        if (!r.enabled || r.limitPerYear === null) return <span className="text-[var(--ink-400)]">—</span>;
        if (full === 0 && near === 0) return <span className="text-[var(--ink-700)]">ยังไม่มีใครใกล้เต็ม</span>;
        return (
          <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-table-sm)]">
            {full > 0 && <span className="inline-flex items-center gap-1 text-[var(--error-text)]"><Icon name="block" size={14} filled /><span className="bo-num">{full}</span> เต็มแล้ว</span>}
            {near > 0 && <span className="inline-flex items-center gap-1 text-[var(--warning-text)]"><Icon name="warning" size={14} filled /><span className="bo-num">{near}</span> ใกล้เต็ม</span>}
          </span>
        );
      },
    },
    { id: 'provisional', header: 'ที่มาของค่า', width: 128, value: (r) => (r.provisional ? 'ค่าสมมติ' : 'ยืนยันแล้ว'), cell: (r) => (r.provisional ? <ProvisionalTag /> : <span className="text-[var(--ink-700)]">ยืนยันแล้ว</span>) },
    { id: 'updatedAt', header: 'แก้ไขล่าสุด', width: 168, value: (r) => `${r.updatedAt} · ${r.updatedBy}`, cell: (r) => <span className="flex flex-col leading-[1.3]"><span className="bo-num" style={{ textAlign: 'left' }}>{r.updatedAt}</span><span className="bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{r.updatedBy}</span></span> },
    {
      id: 'actions', header: 'จัดการ', width: 96, align: 'right', value: () => '',
      cell: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEdit({ rule: r, limitPerYear: r.limitPerYear, warnAtPercent: r.warnAtPercent, enabled: r.enabled }); }}
          className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          แก้ไข
        </button>
      ),
    },
  ], []);

  /* ---------------------------------------------------- การใช้โควตา */

  const filteredUsage = useMemo(() => QUOTA_USAGE.filter((u) => {
    if (catFilter !== 'all' && u.category !== catFilter) return false;
    if (typeFilter !== 'all' && u.memberType !== typeFilter) return false;
    if (usageFilter === 'full' && u.used < u.limit) return false;
    if (usageFilter === 'near' && !(u.used < u.limit && pctOf(u) >= 80)) return false;
    if (query) {
      const q = query.trim().toLowerCase();
      if (!u.member.name.includes(q) && !u.member.memberCode.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [catFilter, typeFilter, usageFilter, query]);

  const sortedUsage = useMemo(() => {
    if (!usageSort) return filteredUsage;
    const dir = usageSort.direction === 'asc' ? 1 : -1;
    const key = (u: QuotaUsage): string | number =>
      usageSort.columnId === 'pct' ? pctOf(u)
        : usageSort.columnId === 'used' ? u.used
          : usageSort.columnId === 'amount' ? u.amount
            : usageSort.columnId === 'blockedCount' ? u.blockedCount
              : usageSort.columnId === 'category' ? CATEGORY_LABEL[u.category]
                : u.member.name;
    return [...filteredUsage].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filteredUsage, usageSort]);

  const usageColumns: Array<Column<QuotaUsage>> = useMemo(() => [
    {
      id: 'member', header: 'สมาชิก', width: 188, pinned: true, required: true, sortable: true,
      value: (u) => u.member.name,
      cell: (u) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-truncate">{u.member.name}</span>
          <span className="bo-num bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]" style={{ textAlign: 'left' }}>{u.member.memberCode}</span>
        </span>
      ),
    },
    { id: 'memberType', header: 'ประเภทสมาชิก', width: 176, value: (u) => MEMBER_TYPE_LABEL[u.memberType] },
    { id: 'category', header: 'หมวดสินค้า', width: 152, sortable: true, value: (u) => CATEGORY_LABEL[u.category] },
    {
      id: 'pct', header: 'การใช้โควตาในรอบปี', width: 380, sortable: true,
      value: (u) => pctOf(u),
      cell: (u) => <UsageBar used={u.used} limit={u.limit} warnAt={limitOf(u.category, u.memberType)?.warnAtPercent ?? 80} />,
    },
    { id: 'amount', header: 'ยอดเงินที่ใช้ไป (บาท)', width: 168, numeric: true, sortable: true, value: (u) => u.amount, cell: (u) => <span className="bo-num">{u.amount.toLocaleString('th-TH')}</span> },
    {
      id: 'blockedCount', header: 'ถูกบล็อกเพราะโควตาเต็ม', width: 192, numeric: true, sortable: true,
      value: (u) => u.blockedCount,
      cell: (u) => (u.blockedCount === 0
        ? <span className="text-[var(--ink-400)]">—</span>
        : <span className="inline-flex items-center justify-end gap-1 text-[var(--error-text)]"><Icon name="block" size={14} filled /><span className="bo-num">{u.blockedCount}</span> ครั้ง</span>),
    },
    { id: 'lastPurchaseAt', header: 'ซื้อครั้งล่าสุด', width: 128, value: (u) => u.lastPurchaseAt, cell: (u) => <span className="bo-num" style={{ textAlign: 'left' }}>{u.lastPurchaseAt}</span> },
  ], [limitOf]);

  const visibleRules = visibleRuleCols.length ? visibleRuleCols : ruleColumns.map((c) => c.id);
  const visibleUsage = visibleUsageCols.length ? visibleUsageCols : usageColumns.map((c) => c.id);

  const usageRows = sortedUsage.slice((page - 1) * pageSize, page * pageSize);

  const summary = useMemo(() => ({
    full: QUOTA_USAGE.filter((u) => u.used >= u.limit).length,
    near: QUOTA_USAGE.filter((u) => u.used < u.limit && pctOf(u) >= 80).length,
    blocked: QUOTA_USAGE.reduce((s, u) => s + u.blockedCount, 0),
    enforced: rules.filter((r) => r.enabled && r.limitPerYear !== null).length,
  }), [rules]);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (catFilter !== 'all') out.push({ id: 'cat', field: 'หมวดสินค้า', display: CATEGORY_LABEL[catFilter] });
    if (typeFilter !== 'all') out.push({ id: 'type', field: 'ประเภทสมาชิก', display: MEMBER_TYPE_LABEL[typeFilter] });
    if (tab === 'usage' && usageFilter !== 'all') out.push({ id: 'usage', field: 'การใช้โควตา', display: usageFilter === 'full' ? 'เต็มแล้ว' : 'ใกล้เต็ม (80% ขึ้นไป)' });
    return out;
  }, [catFilter, typeFilter, usageFilter, tab]);

  const clearAll = () => { setCatFilter('all'); setTypeFilter('all'); setUsageFilter('all'); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = tab === 'rules'
      ? exportRows({ columns: ruleColumns, rows: sortedRules, columnIds: visibleRules.filter((id) => id !== 'actions'), filename: exportFilename('กติกาโควตารายปี'), format })
      : exportRows({ columns: usageColumns, rows: sortedUsage, columnIds: visibleUsage, filename: exportFilename('การใช้โควตารายปี'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว` });
  };

  const saveRule = (e: EditRule, reason?: string) => {
    const before = rules;
    setRules((prev) => prev.map((r) => (r.id === e.rule.id
      ? { ...r, limitPerYear: e.limitPerYear, warnAtPercent: e.warnAtPercent, enabled: e.enabled, provisional: false, updatedAt: '2569-08-21', updatedBy: 'สุดารัตน์ ทองดี' }
      : r)));
    setEdit(null);
    setConfirmSave(null);
    toast.show({
      tone: 'success',
      message: `บันทึกเพดาน ${CATEGORY_LABEL[e.rule.category]} · ${MEMBER_TYPE_LABEL[e.rule.memberType]} แล้ว`,
      undo: { onUndo: () => setRules(before), seconds: 10 },
    });
    void reason;
  };

  const emptyState = activeFilters.length || query
    ? <EmptyState kind="no-results" title="ไม่พบรายการที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
    : <EmptyState kind="no-data" title="ยังไม่มีข้อมูลโควตา" description="ตั้งเพดานต่อหมวดสินค้าและประเภทสมาชิกก่อน แล้วระบบจะเริ่มนับการใช้ตามรอบปี" />;

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="โควตาการซื้อรายปี — กลุ่ม ปตท."
        count={tab === 'rules' ? sortedRules.length : sortedUsage.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder={tab === 'rules' ? 'ค้นหาหมวดสินค้าหรือประเภทสมาชิก' : 'ค้นหาชื่อหรือรหัสสมาชิก'} width={300} />}
        end={
          <>
            <DensityToggle value={density} onChange={setDensity} />
            {tab === 'rules'
              ? <ColumnPicker columns={ruleColumns} persistKey="quota-rules" value={visibleRules} onChange={setVisibleRuleCols} />
              : <ColumnPicker columns={usageColumns} persistKey="quota-usage" value={visibleUsage} onChange={setVisibleUsageCols} />}
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      <div role="alert" className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--error-bg)] px-[var(--space-4)] py-[var(--space-2)]">
        <Icon name="report" size={19} filled className="text-[var(--error-text)]" />
        <p className="m-0 max-w-[106ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          <strong className="text-[var(--error-text)]">ตัวเลขเพดานทุกค่ายังเป็นค่าสมมติ</strong> — BR-09 ระบุว่าต้องจำกัดปริมาณต่อคนต่อปี
          แต่ยังไม่ได้คำตอบว่าเพดานจริงเท่าไร นับเป็นชิ้นหรือเป็นมูลค่า รอบปีเริ่มนับเดือนไหน
          และเมื่อโควตาเต็มระบบควรบล็อกการสั่งซื้อหรือให้ซื้อต่อในราคาปกติ ห้ามใช้ตัวเลขในหน้านี้ตัดสินใจจนได้คำตอบ
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-4)] border-b border-[var(--line-200)] bg-[var(--blue-050)] px-[var(--space-4)] py-[var(--space-2)]">
        {[
          ['เพดานที่บังคับใช้', summary.enforced.toLocaleString('th-TH') + ' กติกา', 'rule', 'var(--blue-700)'],
          ['สมาชิกที่โควตาเต็ม', summary.full.toLocaleString('th-TH') + ' คน', 'block', 'var(--error-text)'],
          ['ใกล้เต็ม (80% ขึ้นไป)', summary.near.toLocaleString('th-TH') + ' คน', 'warning', 'var(--warning-text)'],
          ['ครั้งที่ระบบบล็อกการสั่งซื้อ', summary.blocked.toLocaleString('th-TH') + ' ครั้ง', 'shopping_cart_off', 'var(--ink-700)'],
        ].map(([label, value, icon, color]) => (
          <span key={label} className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            <Icon name={icon!} size={18} style={{ color }} />
            {label}
            <strong className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{value}</strong>
          </span>
        ))}
      </div>

      <div role="tablist" aria-label="มุมมองโควตา" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        {([['rules', 'กติกาโควตา', 'rule', rules.length], ['usage', 'การใช้โควตา', 'monitoring', QUOTA_USAGE.length]] as Array<[Tab, string, string, number]>).map(([id, label, icon, n]) => {
          const on = tab === id;
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => { setTab(id); setPage(1); }}
              className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
              }`}
            >
              <Icon name={icon} size={18} filled={on} />
              {label}
              <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                {n.toLocaleString('th-TH')}
              </span>
            </button>
          );
        })}
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={(id) => {
          if (id === 'cat') setCatFilter('all');
          if (id === 'type') setTypeFilter('all');
          if (id === 'usage') setUsageFilter('all');
          setPage(1);
        }}
        onClearAll={clearAll}
        currentFilters={{ catFilter, typeFilter, usageFilter }}
        persistKey={`quota-${tab}`}
        resultCount={tab === 'rules' ? sortedRules.length : sortedUsage.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          หมวดสินค้า
          <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value as typeof catFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทุกหมวด</option>
            {(Object.keys(CATEGORY_LABEL) as ProductCategory[]).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ประเภทสมาชิก
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as typeof typeFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {PTT_TYPES.map((t) => <option key={t} value={t}>{MEMBER_TYPE_LABEL[t]}</option>)}
          </select>
        </label>
        {tab === 'usage' && (
          <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            การใช้โควตา
            <select value={usageFilter} onChange={(e) => { setUsageFilter(e.target.value as typeof usageFilter); setPage(1); }} className={inputCls}>
              <option value="all">ทั้งหมด</option>
              <option value="full">เต็มแล้ว</option>
              <option value="near">ใกล้เต็ม (80% ขึ้นไป)</option>
            </select>
          </label>
        )}
      </FilterBar>

      {tab === 'rules' ? (
        <DataTable<QuotaRule>
          columns={ruleColumns.filter((c) => visibleRules.includes(c.id))}
          rows={sortedRules}
          rowKey={(r) => r.id}
          caption={`กติกาโควตารายปี — ${sortedRules.length} รายการ`}
          state={screenState}
          rowCountHint={9}
          errorMessage="โหลดกติกาโควตาไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
          onRetry={() => setScreenState('ready')}
          emptyState={emptyState}
          sort={ruleSort}
          onSortChange={setRuleSort}
          onOpenRow={(r) => setEdit({ rule: r, limitPerYear: r.limitPerYear, warnAtPercent: r.warnAtPercent, enabled: r.enabled })}
          density={density}
          persistKey="quota-rules"
          isFlagged={(r) => r.provisional}
          className="min-h-0 flex-1 rounded-none border-0"
        />
      ) : (
        <>
          <DataTable<QuotaUsage>
            columns={usageColumns.filter((c) => visibleUsage.includes(c.id))}
            rows={usageRows}
            rowKey={(u) => u.id}
            caption={`การใช้โควตารายปี — ${sortedUsage.length} รายการ`}
            state={screenState}
            rowCountHint={12}
            errorMessage="โหลดข้อมูลการใช้โควตาไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
            onRetry={() => setScreenState('ready')}
            emptyState={emptyState}
            sort={usageSort}
            onSortChange={setUsageSort}
            onOpenRow={setUsageDetail}
            density={density}
            persistKey="quota-usage"
            isFlagged={(u) => u.used >= u.limit}
            className="min-h-0 flex-1 rounded-none border-0"
          />
          <Pagination page={page} pageSize={pageSize} totalItems={sortedUsage.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[25, 50, 100, 200]} />
        </>
      )}

      {/* -------------------------------------------- แก้ไขเพดานโควตา */}
      <SideDrawer
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title="แก้ไขเพดานโควตา"
        subtitle={edit ? `${CATEGORY_LABEL[edit.rule.category]} · ${MEMBER_TYPE_LABEL[edit.rule.memberType]}` : ''}
      >
        {edit && (() => {
          const affected = QUOTA_USAGE.filter((u) => u.category === edit.rule.category && u.memberType === edit.rule.memberType);
          const overflow = overflowCount(edit);
          const changed = edit.limitPerYear !== edit.rule.limitPerYear || edit.warnAtPercent !== edit.rule.warnAtPercent || edit.enabled !== edit.rule.enabled;
          return (
            <div className="flex flex-col gap-[var(--space-4)]">
              {edit.rule.provisional && (
                <div className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-25)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                  <Icon name="pending_actions" size={18} className="text-[var(--warning-text)]" />
                  <span>ค่าปัจจุบันเป็นค่าสมมติที่ทีมตั้งไว้ ยังไม่ได้รับการยืนยันจากลูกค้า — เมื่อบันทึกค่าใหม่ ระบบจะถือว่าค่านี้ได้รับการยืนยันแล้ว</span>
                </div>
              )}

              <label className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
                <input
                  type="checkbox"
                  checked={edit.enabled}
                  onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })}
                  className="mt-[3px] h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                />
                <span>
                  <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">บังคับใช้เพดานนี้</span>
                  <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    ปิดไว้ = ระบบยังนับการใช้ให้ดู แต่ไม่บล็อกการสั่งซื้อเมื่อเกินเพดาน
                  </span>
                </span>
              </label>

              <label className="flex flex-col gap-[var(--space-1)]">
                <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">เพดานต่อคนต่อปี (ชิ้น)</span>
                <span className="flex items-center gap-[var(--space-2)]">
                  <input
                    data-autofocus
                    type="number"
                    min={0}
                    value={edit.limitPerYear ?? ''}
                    disabled={!edit.enabled}
                    onChange={(e) => setEdit({ ...edit, limitPerYear: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })}
                    className={`${inputCls} bo-num w-[160px] disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)]`}
                  />
                  <button
                    type="button"
                    onClick={() => setEdit({ ...edit, limitPerYear: null })}
                    className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                  >
                    ไม่จำกัด
                  </button>
                </span>
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                  ค่าเดิม: {edit.rule.limitPerYear === null ? 'ไม่จำกัด' : `${edit.rule.limitPerYear.toLocaleString('th-TH')} ชิ้น`}
                </span>
              </label>

              <label className="flex flex-col gap-[var(--space-1)]">
                <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">เตือนผู้ใช้เมื่อใช้ถึง (%)</span>
                <input
                  type="number"
                  min={50}
                  max={99}
                  value={edit.warnAtPercent}
                  disabled={!edit.enabled || edit.limitPerYear === null}
                  onChange={(e) => setEdit({ ...edit, warnAtPercent: Math.min(99, Math.max(50, Number(e.target.value) || 80)) })}
                  className={`${inputCls} bo-num w-[120px] disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)]`}
                />
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                  สมาชิกจะเห็นคำเตือนในแอปเมื่อใช้ถึงเปอร์เซ็นต์นี้ ก่อนถูกบล็อกจริง
                </span>
              </label>

              <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-100)] p-[var(--space-3)]">
                <h3 className="m-0 text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ผลกระทบของค่าใหม่</h3>
                <div className="grid grid-cols-2 gap-[var(--space-3)]">
                  <Field label="สมาชิกในกลุ่มนี้">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{affected.length.toLocaleString('th-TH')} คน</span>
                  </Field>
                  <Field label="จะเกินเพดานใหม่ทันที" hint={overflow > 0 ? 'คนกลุ่มนี้จะถูกบล็อกการสั่งซื้อทันทีที่บันทึก' : 'ไม่มีใครเกินเพดานใหม่'}>
                    <span className={`bo-num ${overflow > 0 ? 'text-[var(--error-text)]' : ''}`} style={{ textAlign: 'left' }}>
                      {overflow.toLocaleString('th-TH')} คน
                    </span>
                  </Field>
                </div>
                {overflow > 0 && (
                  <p className="m-0 flex items-start gap-[var(--space-15)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--error-text)]">
                    <Icon name="priority_high" size={17} filled />
                    ถ้าไม่ต้องการให้ใครถูกบล็อกย้อนหลัง ให้ตั้งเพดานไม่ต่ำกว่ายอดที่คนใช้ไปแล้วมากที่สุด
                    ({Math.max(...affected.map((u) => u.used), 0).toLocaleString('th-TH')} ชิ้น)
                  </p>
                )}
              </section>

              <div className="flex items-center gap-[var(--space-2)]">
                <button
                  type="button"
                  disabled={!changed}
                  onClick={() => setConfirmSave(edit)}
                  className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-5)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--blue-800)] disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  บันทึกเพดาน
                </button>
                <button
                  type="button"
                  onClick={() => setEdit(null)}
                  className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  ยกเลิก
                </button>
                {!changed && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">ยังไม่มีการเปลี่ยนแปลง</span>}
              </div>
            </div>
          );
        })()}
      </SideDrawer>

      {/* ------------------------------------ รายละเอียดการใช้ของสมาชิก */}
      <SideDrawer
        open={Boolean(usageDetail)}
        onClose={() => setUsageDetail(null)}
        title={usageDetail?.member.name ?? ''}
        subtitle={usageDetail ? `${usageDetail.member.memberCode} · ${MEMBER_TYPE_LABEL[usageDetail.memberType]}` : ''}
        footer={usageDetail && (
          <a
            href={`/customers/${usageDetail.member.id}`}
            className="inline-flex min-h-[var(--hit-primary)] items-center rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] no-underline"
          >
            เปิด Customer 360
          </a>
        )}
      >
        {usageDetail && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] p-[var(--space-3)]">
              <h3 className="m-0 text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">
                {CATEGORY_LABEL[usageDetail.category]} — รอบปีนี้
              </h3>
              <UsageBar used={usageDetail.used} limit={usageDetail.limit} warnAt={limitOf(usageDetail.category, usageDetail.memberType)?.warnAtPercent ?? 80} />
              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <Field label="ยอดเงินที่ใช้ไป">
                  <span className="bo-num" style={{ textAlign: 'left' }}>{usageDetail.amount.toLocaleString('th-TH')} บาท</span>
                </Field>
                <Field label="ซื้อครั้งล่าสุด">
                  <span className="bo-num" style={{ textAlign: 'left' }}>{usageDetail.lastPurchaseAt}</span>
                </Field>
                <Field label="คงเหลือในรอบปี">
                  <span className="bo-num" style={{ textAlign: 'left' }}>{Math.max(0, usageDetail.limit - usageDetail.used).toLocaleString('th-TH')} ชิ้น</span>
                </Field>
                <Field label="ถูกบล็อกเพราะโควตาเต็ม" hint={usageDetail.blockedCount > 0 ? 'สมาชิกอาจโทรสอบถามที่ call center' : undefined}>
                  <span className="bo-num" style={{ textAlign: 'left' }}>{usageDetail.blockedCount} ครั้ง</span>
                </Field>
              </div>
            </section>

            {/* การใช้โควตาหมวดอื่นของสมาชิกคนเดียวกัน */}
            <section>
              <h3 className="m-0 mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">หมวดอื่นของสมาชิกคนนี้</h3>
              {QUOTA_USAGE.filter((u) => u.member.id === usageDetail.member.id && u.id !== usageDetail.id).length === 0 ? (
                <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                  ยังไม่มีการซื้อในหมวดอื่นที่มีโควตาในรอบปีนี้
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-[var(--space-2)] p-0">
                  {QUOTA_USAGE.filter((u) => u.member.id === usageDetail.member.id && u.id !== usageDetail.id).map((u) => (
                    <li key={u.id} className="flex flex-col gap-[var(--space-1)]">
                      <span className="text-[length:var(--text-form)] text-[var(--ink-900)]">{CATEGORY_LABEL[u.category]}</span>
                      <UsageBar used={u.used} limit={u.limit} warnAt={limitOf(u.category, u.memberType)?.warnAtPercent ?? 80} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="m-0 border-t border-[var(--line-200)] pt-[var(--space-3)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
              การปรับโควตาให้สมาชิกรายคนยังไม่มีในรอบนี้ — ถ้าจำเป็นต้องมี (เช่น กรณีซื้อให้ครอบครัว)
              ต้องได้นโยบายก่อนว่าใครอนุมัติได้และต้องบันทึกอะไร
            </p>
          </div>
        )}
      </SideDrawer>

      {/* ------------------------------------ ยืนยันก่อนบันทึกเพดานใหม่ */}
      <ConfirmDialog
        open={Boolean(confirmSave)}
        tone={confirmSave && overflowCount(confirmSave) > 0 ? 'danger' : 'default'}
        title="บันทึกเพดานโควตา"
        description={confirmSave ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">
              {CATEGORY_LABEL[confirmSave.rule.category]} · {MEMBER_TYPE_LABEL[confirmSave.rule.memberType]} —{' '}
              เปลี่ยนเพดานจาก{' '}
              <strong className="bo-num">{confirmSave.rule.limitPerYear === null ? 'ไม่จำกัด' : confirmSave.rule.limitPerYear.toLocaleString('th-TH')}</strong>{' '}
              เป็น{' '}
              <strong className="bo-num">{confirmSave.limitPerYear === null ? 'ไม่จำกัด' : confirmSave.limitPerYear.toLocaleString('th-TH')}</strong>
              {confirmSave.enabled ? '' : ' และปิดการบังคับใช้'}
            </p>
            {overflowCount(confirmSave) > 0 && (
              <p className="m-0 font-[var(--weight-medium)] text-[var(--error-text)]">
                สมาชิก {overflowCount(confirmSave).toLocaleString('th-TH')} คนจะเกินเพดานใหม่ทันที
                และถูกบล็อกการสั่งซื้อในราคาพนักงานตั้งแต่วันนี้
              </p>
            )}
          </>
        ) : ''}
        confirmLabel="บันทึกเพดาน"
        requireReason
        /* หน้านี้ยังไม่มี backend — เหตุผลอยู่แค่ในหน้าจอ */
        reasonNote="หน้านี้ยังเป็นข้อมูลจำลอง เหตุผลยังไม่ถูกบันทึกจริง"
        reasonLabel="เหตุผลที่เปลี่ยนเพดาน"
        reasonPresets={[
          'ได้ตัวเลขที่ยืนยันจากลูกค้าแล้ว',
          'ปรับตามนโยบายป้องกันการซื้อไปขายต่อ',
          'แก้ค่าที่ตั้งผิดตั้งแต่แรก',
        ]}
        onConfirm={(reason) => confirmSave && saveRule(confirmSave, reason)}
        onCancel={() => setConfirmSave(null)}
      />
    </div>
  );
};

export default QuotaScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-17  ตั้งค่าและติดตามโควตาการซื้อรายปี แยกตามหมวดสินค้าและประเภทสมาชิก
   BR-09      จำกัดปริมาณการซื้อในราคาพนักงานต่อคนต่อปี เพื่อกันการซื้อไปขายต่อ
   SRS 3.5    เงื่อนไขพิเศษกลุ่ม PTT Group
   FR-MTYPE-06 เพดานผูกกับประเภทสมาชิก เมื่อประเภทเปลี่ยน เพดานที่ใช้เปลี่ยนตาม
   FR-ADM-37  การแก้เพดานทุกครั้งเข้า Audit Log พร้อมเหตุผลและค่าเดิม→ค่าใหม่
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้

   ประเด็นค้าง (PROGRESS-REPORT 8.4 — ⛔ ต้องได้คำตอบก่อนเปิดใช้จริง):
   — เพดานจริงของแต่ละหมวดเป็นเท่าไร (ตอนนี้สมมติ 500/300/200 ชิ้นต่อปี)
   — นับเป็นจำนวนชิ้นหรือมูลค่าเงิน (หน้าจอนี้นับเป็นชิ้น ถ้าเปลี่ยนเป็นมูลค่าต้องแก้ทั้งหน้า)
   — รอบปีเริ่มนับเดือนไหน (ปีปฏิทิน ปีงบประมาณ หรือนับจากวันที่สมัคร)
   — เมื่อโควตาเต็ม ระบบบล็อกการสั่งซื้อ หรือให้ซื้อต่อได้ในราคาปกติ
   — รายการหมวดสินค้าจริงที่ถูกจำกัด (ตอนนี้ใช้ 5 หมวดสมมติ)
   — การปรับโควตารายคนต้องมีหรือไม่ ใครอนุมัติได้ (ยังไม่ทำในรอบนี้)
   ============================================================================ */
